import { z } from 'zod'

/**
 * O contrato do push. docs/06-gamificacao-economia.md secao 21, docs/18-frontend-web2.md secao 39.5,
 * docs/adr/0033.
 *
 * Tres coisas atravessam processos aqui, e por isso moram no contrato:
 *
 * - **os destinos**: o `api` escolhe um ao escrever o aviso, o painel oferece a mesma lista ao
 *   operador, e o service worker do `web2` abre o caminho no toque;
 * - **a inscricao**: o `web2` a produz (`PushSubscription.toJSON()` do navegador) e o `api` a grava;
 * - **a carga util**: o `api` a cifra e o service worker a le.
 *
 * O `admin-web` nao importa daqui (ADR 0009) e duplica a lista de destinos, como ja duplica os
 * limites da arte de produto. Se um destino nascer aqui, nasce la tambem.
 */

/**
 * Para onde o toque na notificacao leva. **Lista fechada, e nunca URL livre**: um campo aberto faria
 * do painel um jeito de mandar o jogador para fora do jogo com a marca do jogo em cima
 * (docs/06 secao 21.5). Os caminhos sao os do `web2` (`apps/web2/src/shared/routes.ts`).
 */
export const PUSH_DESTINATIONS = {
  home: '/',
  news: '/news',
  collection: '/collection',
  market: '/market',
  auctions: '/market/auctions',
  myListings: '/market/me',
  buildChallenges: '/build-challenges',
  prizeTrack: '/prize-track',
  prizeRace: '/prize-race',
  ranking: '/ranking',
  shop: '/shop',
} as const

export type PushDestination = keyof typeof PUSH_DESTINATIONS

export const PUSH_DESTINATION_KEYS = Object.keys(PUSH_DESTINATIONS) as PushDestination[]

export const pushDestinationSchema = z.enum(PUSH_DESTINATION_KEYS as [PushDestination, ...PushDestination[]])

/**
 * Os tetos da inscricao. Um endereco de servico de push tem uns 200 caracteres, e as duas chaves,
 * 88 e 24 em base64url. Os tetos sao folgados de proposito: o que eles barram e o corpo que nao e
 * inscricao nenhuma, e nao a inscricao de um navegador que mudou de formato.
 */
export const PUSH_ENDPOINT_MAX = 1024
export const PUSH_KEY_MAX = 256

const base64url = z
  .string()
  .min(1)
  .max(PUSH_KEY_MAX)
  .regex(/^[A-Za-z0-9_-]+=*$/)

/** O `PushSubscription.toJSON()` do navegador, sem o `expirationTime`, que nenhum navegador preenche. */
export const pushSubscriptionSchema = z.object({
  endpoint: z.string().url().max(PUSH_ENDPOINT_MAX),
  keys: z.object({ p256dh: base64url, auth: base64url }),
})

export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>

/** O que o service worker recebe e mostra (docs/18 secao 39.5). `tag` e o tipo do aviso. */
export const pushPayloadSchema = z.object({
  title: z.string().min(1).max(120),
  body: z.string().max(500),
  url: z.string().startsWith('/'),
  tag: z.string().min(1).max(64),
})

export type PushPayload = z.infer<typeof pushPayloadSchema>
